/**
 * Mystery Gift - Auto-add/remove free gift based on cart conditions
 * 
 * Includes fetch interceptor to prevent gift adds from any external source.
 * Uses MutationObserver to detect cart DOM changes, then checks eligibility.
 * Uses _skipCartInterceptor flag when making its own cart API calls.
 */

(function() {
  'use strict';

  // ─── Read config ──────────────────────────────────────────────
  var configEl = document.getElementById('mystery-gift-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[MysteryGift] Failed to parse config:', e);
    return;
  }

  if (!config.enabled || !config.giftVariantId) return;

  // ─── Cart Protection: Fetch Interceptor ───────────────────────
  var _originalFetch = window.fetch;
  var giftVariantId = config.giftVariantId;

  window.fetch = function(url, options) {
    if (!options || options.method !== 'POST') return _originalFetch.apply(this, arguments);

    var urlStr = (typeof url === 'string') ? url : url.url || '';

    if (window._skipCartInterceptor) return _originalFetch.apply(this, arguments);

    // Block ALL external adds of the gift variant
    if (urlStr.indexOf('/cart/add.js') !== -1 && options.body) {
      try {
        var body = JSON.parse(options.body);
        var items = body.items || [];
        if (body.id) items = [body];

        for (var i = 0; i < items.length; i++) {
          if (String(items[i].id) === String(giftVariantId)) {
            return Promise.resolve(new Response(JSON.stringify({ items: [] }), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            }));
          }
        }
      } catch (e) {}
    }

    // Prevent quantity increase on gift via change.js
    if (urlStr.indexOf('/cart/change.js') !== -1 && options.body) {
      try {
        var changeBody = JSON.parse(options.body);
        if (changeBody.quantity > 1) {
          return _originalFetch('/cart.json', { credentials: 'same-origin' })
            .then(function(res) { return res.json(); })
            .then(function(cart) {
              var lineIndex = (changeBody.line || 1) - 1;
              if (cart.items[lineIndex] && String(cart.items[lineIndex].variant_id) === String(giftVariantId)) {
                var fixedOptions = Object.assign({}, options, {
                  body: JSON.stringify(Object.assign({}, changeBody, { quantity: 1 }))
                });
                return _originalFetch(url, fixedOptions);
              }
              return _originalFetch(url, options);
            });
        }
      } catch (e) {}
    }

    return _originalFetch.apply(this, arguments);
  };

  // ─── Cart Protection: XHR Interceptor ───────────────────────
  var _originalXHROpen = XMLHttpRequest.prototype.open;
  var _originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url) {
    this._mgMethod = method;
    this._mgUrl = url;
    return _originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(body) {
    if (window._skipCartInterceptor) return _originalXHRSend.apply(this, arguments);

    var url = this._mgUrl || '';
    var method = (this._mgMethod || '').toUpperCase();

    // Block external adds of the gift variant via XHR
    if (method === 'POST' && url.indexOf('/cart/add.js') !== -1 && body) {
      try {
        var parsed = typeof body === 'string' ? JSON.parse(body) : body;
        var items = parsed.items || [];
        if (parsed.id) items = [parsed];

        for (var i = 0; i < items.length; i++) {
          if (String(items[i].id) === String(giftVariantId)) {
            console.warn('[MysteryGift] Blocked external XHR add of gift variant');
            // Fake a successful response
            var self = this;
            setTimeout(function() {
              Object.defineProperty(self, 'readyState', { value: 4, writable: true });
              Object.defineProperty(self, 'status', { value: 200, writable: true });
              Object.defineProperty(self, 'responseText', { value: '{"items":[]}', writable: true });
              if (typeof self.onreadystatechange === 'function') self.onreadystatechange();
              if (typeof self.onload === 'function') self.onload();
            }, 10);
            return;
          }
        }
      } catch (e) {}
    }

    // Block quantity increase on gift via XHR change.js
    if (method === 'POST' && url.indexOf('/cart/change.js') !== -1 && body) {
      try {
        var changeBody = typeof body === 'string' ? JSON.parse(body) : body;
        if (changeBody.quantity > 1 && String(changeBody.id) === String(giftVariantId)) {
          var fixedBody = JSON.stringify(Object.assign({}, changeBody, { quantity: 1 }));
          return _originalXHRSend.call(this, fixedBody);
        }
      } catch (e) {}
    }

    return _originalXHRSend.apply(this, arguments);
  };

  // ─── Helper: format money ─────────────────────────────────────
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = config.moneyFormat;
    return format
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{ amount_no_decimals }}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{ amount_with_comma_separator }}', amount.replace('.', ','));
  }

  // ─── Cart API helpers ─────────────────────────────────────────
  function getCart() {
    return fetch('/cart.json', { credentials: 'same-origin' })
      .then(function(res) { return res.json(); });
  }

  function addGift() {
    window._skipCartInterceptor = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: config.giftVariantId,
          quantity: 1,
          properties: { '_mystery_gift': 'true', '_source': 'mystery-gift-auto' }
        }]
      }),
      credentials: 'same-origin'
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      window._skipCartInterceptor = false;
      return data;
    })
    .catch(function(err) {
      window._skipCartInterceptor = false;
      console.error('[MysteryGift] Failed to add gift:', err);
      throw err;
    });
  }

  function removeGift(cart) {
    var giftLines = getGiftLines(cart);
    if (giftLines.length === 0) return Promise.resolve(null);

    var updates = {};
    for (var i = 0; i < giftLines.length; i++) {
      updates[giftLines[i].key] = 0;
    }

    window._skipCartInterceptor = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
      credentials: 'same-origin'
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      window._skipCartInterceptor = false;
      return data;
    })
    .catch(function(err) {
      window._skipCartInterceptor = false;
      console.error('[MysteryGift] Failed to remove gift:', err);
      throw err;
    });
  }

  // ─── Cart helpers ─────────────────────────────────────────────
  function giftInCart(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].properties && cart.items[i].properties._mystery_gift === 'true') {
        return true;
      }
      if (String(cart.items[i].variant_id) === String(config.giftVariantId)) {
        return true;
      }
    }
    return false;
  }

  function getGiftLines(cart) {
    var lines = [];
    for (var i = 0; i < cart.items.length; i++) {
      var hasProp = cart.items[i].properties && cart.items[i].properties._mystery_gift === 'true';
      var isVariant = String(cart.items[i].variant_id) === String(config.giftVariantId);
      if (hasProp || isVariant) {
        lines.push({ index: i, quantity: cart.items[i].quantity, key: cart.items[i].key, hasProperty: hasProp });
      }
    }
    return lines;
  }

  function deduplicateGifts(cart) {
    var giftLines = getGiftLines(cart);
    if (giftLines.length === 0) return Promise.resolve(false);

    var needsFix = false;
    var updates = {};

    if (giftLines[0].quantity > 1) {
      updates[giftLines[0].key] = 1;
      needsFix = true;
    }

    for (var i = 1; i < giftLines.length; i++) {
      updates[giftLines[i].key] = 0;
      needsFix = true;
    }

    if (!needsFix) return Promise.resolve(false);

    window._skipCartInterceptor = true;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
      credentials: 'same-origin'
    })
    .then(function(res) { return res.json(); })
    .then(function() {
      window._skipCartInterceptor = false;
      return true;
    })
    .catch(function(err) {
      window._skipCartInterceptor = false;
      console.error('[MysteryGift] Dedup failed:', err);
      return false;
    });
  }

  function getNonGiftItems(cart) {
    return cart.items.filter(function(item) {
      if (item.properties && item.properties._mystery_gift === 'true') return false;
      if (String(item.variant_id) === String(config.giftVariantId)) return false;
      return true;
    });
  }

  function getQualifyingTotal(cart) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var isGiftProp = cart.items[i].properties && cart.items[i].properties._mystery_gift === 'true';
      var isGiftVariant = String(cart.items[i].variant_id) === String(config.giftVariantId);
      if (!isGiftProp && !isGiftVariant) {
        total += cart.items[i].final_line_price;
      }
    }
    return total;
  }

  // ─── Eligibility ──────────────────────────────────────────────
  function checkEligibility(cart) {
    var items = getNonGiftItems(cart);
    var total = getQualifyingTotal(cart);

    if (items.length === 0) return { eligible: false, reason: 'empty' };

    if (config.eligibleProductsEnabled && config.eligibleProductHandles.length > 0) {
      var hasEligible = false;
      for (var i = 0; i < items.length; i++) {
        if (config.eligibleProductHandles.indexOf(items[i].handle) !== -1) {
          hasEligible = true;
          break;
        }
      }
      if (!hasEligible) return { eligible: false, reason: 'products' };
    }

    if (config.eligibleCollectionsEnabled && config.collectionProductIds.length > 0) {
      var hasCollectionProduct = false;
      for (var i = 0; i < items.length; i++) {
        if (config.collectionProductIds.indexOf(items[i].product_id) !== -1) {
          hasCollectionProduct = true;
          break;
        }
      }
      if (!hasCollectionProduct) return { eligible: false, reason: 'collections' };
    }

    if (config.exclusionsEnabled && config.excludedProductHandles.length > 0) {
      for (var i = 0; i < items.length; i++) {
        if (config.excludedProductHandles.indexOf(items[i].handle) !== -1) {
          return { eligible: false, reason: 'excluded' };
        }
      }
    }

    if (config.thresholdEnabled && config.threshold > 0) {
      if (total < config.threshold) return { eligible: false, reason: 'threshold' };
    }

    return { eligible: true, reason: 'eligible' };
  }

  // ─── Banner update ────────────────────────────────────────────
  function updateBanner(cart) {
    var banners = document.querySelectorAll('.mystery-gift-banner');
    if (banners.length === 0) return;

    var items = getNonGiftItems(cart);
    var total = getQualifyingTotal(cart);
    var hasGift = giftInCart(cart);
    var result = checkEligibility(cart);

    for (var b = 0; b < banners.length; b++) {
      var banner = banners[b];
      var messageEl = banner.querySelector('.mystery-gift-banner__text');
      if (!messageEl) continue;

      if (items.length === 0) {
        banner.className = 'mystery-gift-banner mystery-gift-banner--hidden';
      } else if (hasGift && result.eligible) {
        banner.className = 'mystery-gift-banner mystery-gift-banner--unlocked';
        messageEl.innerHTML = config.message;
      } else if (!result.eligible && result.reason !== 'empty' && result.reason !== 'excluded') {
        banner.className = 'mystery-gift-banner mystery-gift-banner--progress';
        var msg = config.progressMessage;
        if (config.thresholdEnabled && config.threshold > 0) {
          var remaining = config.threshold - total;
          if (remaining > 0) msg = msg.replace('{amount}', formatMoney(remaining));
        }
        messageEl.innerHTML = msg;
      } else {
        banner.className = 'mystery-gift-banner mystery-gift-banner--hidden';
      }
    }
  }

  // ─── Section refresh ──────────────────────────────────────────
  function refreshSections() {
    var sections = ['cart-drawer', 'cart-counter'];
    var mainCartEl = document.getElementById('main-cart-items');
    if (mainCartEl && mainCartEl.dataset.id) sections.push(mainCartEl.dataset.id);

    return fetch(window.location.pathname + '?sections=' + sections.join(','), {
      credentials: 'same-origin'
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      if (data['cart-drawer']) {
        var drawerBody = document.getElementById('CartDrawer-Body');
        if (drawerBody) {
          var parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          var newBody = parsed.querySelector('#CartDrawer-Body');
          if (newBody) drawerBody.innerHTML = newBody.innerHTML;
        }
      }

      if (data['cart-counter']) {
        var counterSection = document.getElementById('shopify-section-cart-counter');
        if (counterSection) {
          var parsed2 = new DOMParser().parseFromString(data['cart-counter'], 'text/html');
          var newCounter = parsed2.querySelector('#shopify-section-cart-counter');
          if (newCounter) counterSection.innerHTML = newCounter.innerHTML;
        }
      }

      if (mainCartEl && mainCartEl.dataset.id && data[mainCartEl.dataset.id]) {
        var sectionId = mainCartEl.dataset.id;
        var sectionEl = document.querySelector('#shopify-section-' + sectionId);
        if (sectionEl) {
          var parsed3 = new DOMParser().parseFromString(data[sectionId], 'text/html');
          var newSection = parsed3.querySelector('#shopify-section-' + sectionId + ' cart-items');
          if (newSection) {
            var existing = sectionEl.querySelector('cart-items');
            if (existing) existing.innerHTML = newSection.innerHTML;
          }
        }
      }
    })
    .catch(function(err) {
      console.error('[MysteryGift] Section refresh failed:', err);
    });
  }

  // ─── Main check ───────────────────────────────────────────────
  var isProcessing = false;

  function check() {
    if (isProcessing) return;
    isProcessing = true;

    getCart()
      .then(function(cart) {
        return deduplicateGifts(cart).then(function(didFix) {
          return didFix ? getCart() : cart;
        });
      })
      .then(function(cart) {
        var result = checkEligibility(cart);
        var hasGift = giftInCart(cart);

        if (result.eligible && !hasGift) {
          return getCart().then(function(freshCart) {
            if (giftInCart(freshCart)) {
              updateBanner(freshCart);
              return;
            }
            return addGift().then(function() {
              return refreshSections();
            }).then(function() {
              return getCart();
            }).then(function(updatedCart) {
              updateBanner(updatedCart);
            });
          });
        } else if (!result.eligible && hasGift) {
          return removeGift(cart).then(function() {
            return refreshSections();
          }).then(function() {
            return getCart();
          }).then(function(updatedCart) {
            updateBanner(updatedCart);
          });
        } else {
          updateBanner(cart);
        }
      })
      .catch(function(err) {
        console.error('[MysteryGift] Check failed:', err);
      })
      .then(function() {
        isProcessing = false;
      });
  }

  window.MysteryGift = {
    check: check,
    getCart: getCart,
    checkEligibility: checkEligibility,
    config: config
  };

  // ─── Detection: MutationObserver on cart drawer ───────────────
  var checkTimeout = null;

  function scheduleCheck() {
    if (checkTimeout) clearTimeout(checkTimeout);
    checkTimeout = setTimeout(function() { check(); }, 300);
  }

  var drawerBody = document.getElementById('CartDrawer-Body');
  if (drawerBody) {
    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length > 0) {
          if (!isProcessing) scheduleCheck();
          break;
        }
      }
    }).observe(drawerBody, { childList: true, subtree: false });
  }

  var mainCartItems = document.querySelector('.cart__items');
  if (mainCartItems) {
    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length > 0) {
          if (!isProcessing) scheduleCheck();
          break;
        }
      }
    }).observe(mainCartItems, { childList: true, subtree: false });
  }

  // ─── Initial check on page load ──────────────────────────────
  setTimeout(function() { check(); }, 500);

})();